/**
 * AiModelCenterPage — 大模型配置中心管理后台首页（Issue #187）。
 *
 * 体验 / 安全红线：
 *   - 所有数据通过 `@jinzhuan/api-client` 调用后端；禁止手写 fetch / 纯前端假状态。
 *   - 创建凭证时输入框为 password 类型，提交后立刻清空表单值；
 *     列表只展示 alias / hint / scope，绝不展示原文。
 *   - 模型版本字段是开放文本（接受 gpt-5.5 / claude-sonnet-4.5 等任意值）。
 *   - 价格 / 调用成本字段直接展示后端返回的 Decimal 字符串，
 *     不在前端做 parseFloat / sum 计算。
 */
import {
  useCallback,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  triggerProviderSync,
  useLlmCallAudits,
  useLlmCredentials,
  useLlmModels,
  useLlmProviders,
  type CreateLlmCredentialRequest,
  type CreateLlmProviderRequest,
  type LlmProviderDto,
  type UpsertLlmModelRequest,
} from '@/hooks/useAiModelCenter';
import {
  LLM_PROVIDER_TYPE_LABELS,
  LLM_AUTH_MODE_LABELS,
  LLM_DATA_RESIDENCY_LABELS,
  LLM_CREDENTIAL_SCOPE_LABELS,
  LLM_MODEL_SOURCE_LABELS,
  LLM_CALL_STATUS_LABELS,
  LLM_TRIGGER_TYPE_LABELS,
  LLM_REVIEW_STATUS_LABELS,
  formatEnumLabel,
} from '@/lib/enumLabels';

const PROVIDER_TYPE_OPTIONS: ReadonlyArray<{
  value: CreateLlmProviderRequest['providerType'];
  label: string;
}> = [
  { value: 'official_cloud', label: '官方云 API' },
  { value: 'proxy_aggregator', label: '代理聚合' },
  { value: 'local_private', label: '本地私有' },
  { value: 'custom', label: '自定义' },
];

const AUTH_MODE_OPTIONS: ReadonlyArray<{
  value: CreateLlmProviderRequest['authMode'];
  label: string;
}> = [
  { value: 'bearer_token', label: 'Bearer Token' },
  { value: 'api_key_header', label: 'API Key Header' },
  { value: 'custom_header', label: '自定义请求头' },
  { value: 'none', label: '无（仅本地私有）' },
];

const DATA_RESIDENCY_OPTIONS: ReadonlyArray<{
  value: CreateLlmProviderRequest['dataResidency'];
  label: string;
}> = [
  { value: 'domestic', label: '境内' },
  { value: 'cross_border', label: '跨境' },
  { value: 'local_private', label: '本地私有' },
];

const CAPABILITY_OPTIONS: ReadonlyArray<{
  value: UpsertLlmModelRequest['capabilities'][number];
  label: string;
}> = [
  { value: 'general_chat', label: '通用问答' },
  { value: 'medical_assistant', label: 'AI 问诊助理' },
  { value: 'disease_progression_prediction', label: '病情趋势预测' },
  { value: 'cell_intervention_prognosis', label: '细胞干预预后' },
  { value: 'ai_detection', label: 'AI 检测/报告解析' },
  { value: 'image_understanding', label: '图像理解' },
  { value: 'image_to_image', label: '图生图' },
  { value: 'text_to_image', label: '文生图' },
  { value: 'report_visualization', label: '报告可视化' },
  { value: 'finance_insight', label: '财务洞察' },
  { value: 'inventory_insight', label: '物料洞察' },
  { value: 'ocr_extract', label: 'OCR 抽取' },
  { value: 'long_context', label: '长上下文' },
  { value: 'tool_calling', label: '工具调用' },
  { value: 'local_private', label: '本地私有' },
];

const SCOPE_OPTIONS: ReadonlyArray<{
  value: CreateLlmCredentialRequest['scope'];
  label: string;
}> = [
  { value: 'shared', label: '共享' },
  { value: 'internal_only', label: '仅内部使用' },
  { value: 'user_only', label: '仅创建者' },
];

export default function AiModelCenterPage(): ReactElement {
  const providersState = useLlmProviders();
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  const selectedProvider = useMemo<LlmProviderDto | null>(() => {
    if (!selectedProviderId || !providersState.data) return null;
    return providersState.data.find((p) => p.id === selectedProviderId) ?? null;
  }, [providersState.data, selectedProviderId]);

  const modelsState = useLlmModels(selectedProviderId);
  const credentialsState = useLlmCredentials(selectedProviderId);
  const auditsState = useLlmCallAudits({ providerId: selectedProviderId });

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="ai-model-center-page">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">大模型配置中心</h1>
        <p className="text-sm text-muted-foreground">
          维护平台、模型版本、API 凭证（加密存储）、调用配额与 AI 调用审计。
          凭证明文不会出现在任何 API 响应或本页面；列表仅展示别名与脱敏 hint。
        </p>
      </div>

      <CreateProviderCard
        loading={providersState.loading}
        onCreate={providersState.createProvider}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">已配置平台</CardTitle>
        </CardHeader>
        <CardContent>
          {providersState.loading ? (
            <div className="text-sm text-muted-foreground">加载中…</div>
          ) : providersState.error ? (
            <div className="text-sm text-destructive" data-testid="providers-error">
              {providersState.error}
            </div>
          ) : !providersState.data || providersState.data.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无平台，请先创建。</div>
          ) : (
            <Table data-testid="providers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>平台短码</TableHead>
                  <TableHead>展示名</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>数据出境</TableHead>
                  <TableHead>启用</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providersState.data.map((provider) => (
                  <TableRow
                    key={provider.id}
                    data-testid={`provider-row-${provider.code}`}
                  >
                    <TableCell className="font-mono text-xs">{provider.code}</TableCell>
                    <TableCell>{provider.displayName}</TableCell>
                    <TableCell>{formatEnumLabel(provider.providerType, LLM_PROVIDER_TYPE_LABELS, '未知类型')}</TableCell>
                    <TableCell>{formatEnumLabel(provider.dataResidency, LLM_DATA_RESIDENCY_LABELS, '未知')}</TableCell>
                    <TableCell>{provider.isEnabled ? '是' : '否'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={selectedProviderId === provider.id ? 'default' : 'ghost'}
                        onClick={() => setSelectedProviderId(provider.id)}
                        data-testid={`provider-select-${provider.code}`}
                      >
                        管理
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedProvider ? (
        <ProviderManagementBlock
          provider={selectedProvider}
          onSync={async () => {
            const result = await triggerProviderSync(selectedProvider.id);
            await modelsState.refresh();
            return result;
          }}
          modelsState={modelsState}
          credentialsState={credentialsState}
          auditsState={auditsState}
        />
      ) : (
        <div className="text-sm text-muted-foreground">
          在上方平台列表中点击「管理」，进入模型 / 凭证 / 调用审计明细。
        </div>
      )}
    </div>
  );
}

// =============================================================================
// 创建平台表单
// =============================================================================

interface CreateProviderCardProps {
  loading: boolean;
  onCreate: (input: CreateLlmProviderRequest) => Promise<LlmProviderDto | null>;
}

function CreateProviderCard({ loading, onCreate }: CreateProviderCardProps): ReactElement {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [providerType, setProviderType] = useState<CreateLlmProviderRequest['providerType']>(
    'official_cloud',
  );
  const [authMode, setAuthMode] = useState<CreateLlmProviderRequest['authMode']>('bearer_token');
  const [dataResidency, setDataResidency] = useState<CreateLlmProviderRequest['dataResidency']>(
    'cross_border',
  );
  const [baseUrl, setBaseUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await onCreate({
          code: code.trim(),
          displayName: displayName.trim(),
          providerType,
          authMode,
          dataResidency,
          baseUrl: baseUrl.trim() || null,
          isEnabled: true,
          defaultTimeoutMs: 60_000,
          defaultConcurrency: 5,
        });
        setCode('');
        setDisplayName('');
        setBaseUrl('');
      } catch (err) {
        const message = err instanceof Error ? err.message : '创建平台失败';
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [authMode, baseUrl, code, dataResidency, displayName, onCreate, providerType],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">新增平台</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={handleSubmit}
          data-testid="create-provider-form"
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-code">平台短码（租户内唯一）</Label>
            <Input
              id="provider-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="openai"
              required
              data-testid="provider-code-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-display-name">展示名</Label>
            <Input
              id="provider-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="OpenAI 官方"
              required
              data-testid="provider-display-name-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-type">类型</Label>
            <select
              id="provider-type"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={providerType}
              onChange={(e) =>
                setProviderType(e.target.value as CreateLlmProviderRequest['providerType'])
              }
            >
              {PROVIDER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-auth-mode">鉴权方式</Label>
            <select
              id="provider-auth-mode"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={authMode}
              onChange={(e) =>
                setAuthMode(e.target.value as CreateLlmProviderRequest['authMode'])
              }
            >
              {AUTH_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-residency">数据出境风险</Label>
            <select
              id="provider-residency"
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={dataResidency}
              onChange={(e) =>
                setDataResidency(e.target.value as CreateLlmProviderRequest['dataResidency'])
              }
            >
              {DATA_RESIDENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="provider-base-url">Base URL（可选）</Label>
            <Input
              id="provider-base-url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.openai.com/v1"
            />
          </div>
          {error ? (
            <div className="col-span-1 text-sm text-destructive sm:col-span-2" data-testid="create-provider-error">
              {error}
            </div>
          ) : null}
          <div className="col-span-1 sm:col-span-2">
            <Button type="submit" disabled={submitting || loading} data-testid="create-provider-submit">
              {submitting ? '创建中…' : '创建平台'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// 平台管理（模型 / 凭证 / 调用审计）
// =============================================================================

interface ProviderManagementBlockProps {
  provider: LlmProviderDto;
  onSync: () => Promise<unknown>;
  modelsState: ReturnType<typeof useLlmModels>;
  credentialsState: ReturnType<typeof useLlmCredentials>;
  auditsState: ReturnType<typeof useLlmCallAudits>;
}

function ProviderManagementBlock({
  provider,
  onSync,
  modelsState,
  credentialsState,
  auditsState,
}: ProviderManagementBlockProps): ReactElement {
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {provider.displayName} ({provider.code})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>类型：{formatEnumLabel(provider.providerType, LLM_PROVIDER_TYPE_LABELS, '未知类型')}</span>
            <span>数据出境：{formatEnumLabel(provider.dataResidency, LLM_DATA_RESIDENCY_LABELS, '未知')}</span>
            <span>鉴权：{formatEnumLabel(provider.authMode, LLM_AUTH_MODE_LABELS, '未知')}</span>
            <span>Base URL：{provider.baseUrl ?? '—'}</span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="default"
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                setSyncError(null);
                setSyncSummary(null);
                try {
                  const result = (await onSync()) as {
                    run?: {
                      status: string;
                      fetchedCount: number;
                      upsertedCount: number;
                      errorMessage?: string | null;
                    };
                    skippedManualModelIds?: ReadonlyArray<string>;
                  };
                  if (result?.run) {
                    const parts = [
                      `同步结果：${result.run.status}`,
                      `获取 ${result.run.fetchedCount} 个模型`,
                      `写入 ${result.run.upsertedCount} 条`,
                    ];
                    const skipped = result.skippedManualModelIds?.length ?? 0;
                    if (skipped > 0) {
                      parts.push(`保留手动记录 ${skipped} 条`);
                    }
                    setSyncSummary(`${parts.join('，')}。`);
                    if (result.run.errorMessage) {
                      setSyncError(result.run.errorMessage);
                    }
                  }
                } catch (err) {
                  setSyncError(err instanceof Error ? err.message : '同步失败');
                } finally {
                  setSyncing(false);
                }
              }}
              data-testid="provider-sync-button"
            >
              {syncing ? '同步中…' : '同步模型列表'}
            </Button>
            {syncError ? (
              <span className="text-sm text-destructive" data-testid="provider-sync-error">
                {syncError}
              </span>
            ) : null}
            {syncSummary ? (
              <span className="text-sm text-muted-foreground" data-testid="provider-sync-summary">
                {syncSummary}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">模型列表</CardTitle>
        </CardHeader>
        <CardContent>
          <UpsertModelForm
            providerId={provider.id}
            onSubmit={modelsState.upsertModel}
          />
          <ModelsTable models={modelsState.data ?? []} loading={modelsState.loading} error={modelsState.error} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">凭证（密文存储，明文不返回）</CardTitle>
        </CardHeader>
        <CardContent>
          <CreateCredentialForm onSubmit={credentialsState.createCredential} />
          <CredentialsTable
            credentials={credentialsState.data ?? []}
            loading={credentialsState.loading}
            error={credentialsState.error}
            onDelete={credentialsState.deleteCredential}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">AI 调用审计</CardTitle>
        </CardHeader>
        <CardContent>
          {auditsState.loading ? (
            <div className="text-sm text-muted-foreground">加载中…</div>
          ) : auditsState.error ? (
            <div className="text-sm text-destructive" data-testid="audits-error">
              {auditsState.error}
            </div>
          ) : !auditsState.data || auditsState.data.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无调用记录。</div>
          ) : (
            <Table data-testid="audits-table">
              <TableHeader>
                <TableRow>
                  <TableHead>时间</TableHead>
                  <TableHead>模型</TableHead>
                  <TableHead>用途</TableHead>
                  <TableHead>触发</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="text-right">输入 token</TableHead>
                  <TableHead className="text-right">输出 token</TableHead>
                  <TableHead className="text-right">成本（Decimal）</TableHead>
                  <TableHead>审核</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditsState.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs">{new Date(row.calledAt).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-xs">{row.modelId}</TableCell>
                    <TableCell>{row.purpose}</TableCell>
                    <TableCell className="text-xs">{formatEnumLabel(row.triggerType, LLM_TRIGGER_TYPE_LABELS, '未知触发')}</TableCell>
                    <TableCell>{formatEnumLabel(row.status, LLM_CALL_STATUS_LABELS, '未知状态')}</TableCell>
                    <TableCell className="text-right">{row.tokenInput}</TableCell>
                    <TableCell className="text-right">{row.tokenOutput}</TableCell>
                    <TableCell className="text-right">
                      {row.estimatedCost} {row.costCurrency}
                    </TableCell>
                    <TableCell>{formatEnumLabel(row.reviewStatus, LLM_REVIEW_STATUS_LABELS, '未知审核状态')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// 模型 upsert
// =============================================================================

interface UpsertModelFormProps {
  providerId: string;
  onSubmit: (input: UpsertLlmModelRequest) => Promise<unknown>;
}

function UpsertModelForm({ providerId, onSubmit }: UpsertModelFormProps): ReactElement {
  const [modelId, setModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [capability, setCapability] = useState<UpsertLlmModelRequest['capabilities'][number]>(
    'general_chat',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await onSubmit({
          providerId,
          modelId: modelId.trim(),
          displayName: displayName.trim(),
          capabilities: [capability],
          inputModalities: ['text'],
          outputModalities: ['text'],
          pricingCurrency: 'CNY',
          isDefault: false,
          isEnabled: true,
          internalOnly: true,
          allowCustomerVisible: false,
          source: 'manual',
        });
        setModelId('');
        setDisplayName('');
      } catch (err) {
        setError(err instanceof Error ? err.message : '保存模型失败');
      } finally {
        setSubmitting(false);
      }
    },
    [capability, displayName, modelId, onSubmit, providerId],
  );

  return (
    <form
      className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4"
      onSubmit={handleSubmit}
      data-testid="upsert-model-form"
    >
      <div className="flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor="model-id">模型版本字符串（接受任意值）</Label>
        <Input
          id="model-id"
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          placeholder="例如 gpt-5.5 / claude-sonnet-4.5"
          required
          data-testid="model-id-input"
        />
      </div>
      <div className="flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor="model-display-name">展示名</Label>
        <Input
          id="model-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
      </div>
      <div className="flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor="model-capability">能力标签</Label>
        <select
          id="model-capability"
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={capability}
          onChange={(e) =>
            setCapability(e.target.value as UpsertLlmModelRequest['capabilities'][number])
          }
        >
          {CAPABILITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-end gap-2 sm:col-span-1">
        <Button type="submit" disabled={submitting} data-testid="upsert-model-submit">
          {submitting ? '保存中…' : '保存模型'}
        </Button>
      </div>
      {error ? (
        <div className="text-sm text-destructive sm:col-span-4" data-testid="upsert-model-error">
          {error}
        </div>
      ) : null}
    </form>
  );
}

interface ModelsTableProps {
  models: readonly NonNullable<ReturnType<typeof useLlmModels>['data']>[number][];
  loading: boolean;
  error: string | null;
}

function ModelsTable({ models, loading, error }: ModelsTableProps): ReactElement {
  if (loading) return <div className="text-sm text-muted-foreground">加载中…</div>;
  if (error)
    return (
      <div className="text-sm text-destructive" data-testid="models-error">
        {error}
      </div>
    );
  if (models.length === 0) return <div className="text-sm text-muted-foreground">暂无模型。</div>;
  return (
    <Table data-testid="models-table">
      <TableHeader>
        <TableRow>
          <TableHead>模型版本</TableHead>
          <TableHead>展示名</TableHead>
          <TableHead>能力</TableHead>
          <TableHead>输入价（Decimal）</TableHead>
          <TableHead>输出价（Decimal）</TableHead>
          <TableHead>启用</TableHead>
          <TableHead>来源</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {models.map((m) => (
          <TableRow key={m.id}>
            <TableCell className="font-mono text-xs">{m.modelId}</TableCell>
            <TableCell>{m.displayName}</TableCell>
            <TableCell className="text-xs">{(m.capabilities ?? []).join(', ')}</TableCell>
            <TableCell>{m.pricingInputPerMillion ?? '—'}</TableCell>
            <TableCell>{m.pricingOutputPerMillion ?? '—'}</TableCell>
            <TableCell>{m.isEnabled ? '是' : '否'}</TableCell>
            <TableCell>{formatEnumLabel(m.source, LLM_MODEL_SOURCE_LABELS, '未知来源')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// =============================================================================
// 凭证
// =============================================================================

interface CreateCredentialFormProps {
  onSubmit: (input: CreateLlmCredentialRequest) => Promise<unknown>;
}

function CreateCredentialForm({ onSubmit }: CreateCredentialFormProps): ReactElement {
  const [alias, setAlias] = useState('');
  const [secret, setSecret] = useState('');
  const [scope, setScope] = useState<CreateLlmCredentialRequest['scope']>('shared');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await onSubmit({
          alias: alias.trim(),
          secret,
          scope,
        });
        // 立刻清空 secret，避免明文残留在 DOM/state。
        setAlias('');
        setSecret('');
      } catch (err) {
        setError(err instanceof Error ? err.message : '创建凭证失败');
      } finally {
        setSubmitting(false);
      }
    },
    [alias, onSubmit, scope, secret],
  );

  return (
    <form
      className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4"
      onSubmit={handleSubmit}
      data-testid="create-credential-form"
    >
      <div className="flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor="cred-alias">别名</Label>
        <Input
          id="cred-alias"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          placeholder="prod / dev / 备份"
          required
          data-testid="cred-alias-input"
        />
      </div>
      <div className="flex flex-col gap-1 sm:col-span-2">
        <Label htmlFor="cred-secret">API Key（明文，提交后立即加密）</Label>
        <Input
          id="cred-secret"
          type="password"
          autoComplete="new-password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          minLength={8}
          required
          data-testid="cred-secret-input"
        />
      </div>
      <div className="flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor="cred-scope">使用范围</Label>
        <select
          id="cred-scope"
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={scope}
          onChange={(e) => setScope(e.target.value as CreateLlmCredentialRequest['scope'])}
        >
          {SCOPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-4">
        <Button type="submit" disabled={submitting} data-testid="create-credential-submit">
          {submitting ? '保存中…' : '保存凭证（明文不会返回）'}
        </Button>
        {error ? (
          <span className="ml-3 text-sm text-destructive" data-testid="create-credential-error">
            {error}
          </span>
        ) : null}
      </div>
    </form>
  );
}

interface CredentialsTableProps {
  credentials: readonly NonNullable<ReturnType<typeof useLlmCredentials>['data']>[number][];
  loading: boolean;
  error: string | null;
  onDelete: (credId: string) => Promise<boolean>;
}

function CredentialsTable({
  credentials,
  loading,
  error,
  onDelete,
}: CredentialsTableProps): ReactElement {
  if (loading) return <div className="text-sm text-muted-foreground">加载中…</div>;
  if (error)
    return (
      <div className="text-sm text-destructive" data-testid="credentials-error">
        {error}
      </div>
    );
  if (credentials.length === 0)
    return <div className="text-sm text-muted-foreground">暂无凭证。</div>;
  return (
    <Table data-testid="credentials-table">
      <TableHeader>
        <TableRow>
          <TableHead>别名</TableHead>
          <TableHead>密钥 hint</TableHead>
          <TableHead>cipher kid</TableHead>
          <TableHead>范围</TableHead>
          <TableHead>启用</TableHead>
          <TableHead>最近使用</TableHead>
          <TableHead className="text-right">操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {credentials.map((c) => (
          <TableRow key={c.id}>
            <TableCell>{c.alias}</TableCell>
            <TableCell className="font-mono text-xs">
              {c.secretHint ? `••••${c.secretHint}` : '****'}
            </TableCell>
            <TableCell className="text-xs">{c.cipherKid}</TableCell>
            <TableCell>{formatEnumLabel(c.scope, LLM_CREDENTIAL_SCOPE_LABELS, '未知范围')}</TableCell>
            <TableCell>{c.isEnabled ? '是' : '否'}</TableCell>
            <TableCell className="text-xs">
              {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : '—'}
            </TableCell>
            <TableCell className="text-right">
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  await onDelete(c.id);
                }}
                data-testid={`credential-delete-${c.id}`}
              >
                禁用
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
